/**
 * @fileoverview Present the full payment history as one chronological activity stream. Legacy verification decisions and server audit events remain separate source records, merged only for display. Human summaries carry the default view; expandable audit details retain every returned value. This surface never derives current permissions or verification status from historical events; `PortalResult.js` owns current context and actions.
 */
import React from 'react';
import { AppButton } from '../shared';
import './PaymentVerificationHistory.css';

const ACTIONS = {
  assignment_pending: 'Verification queued', assignment_undone: 'Assignment undone',
  assignment_revoked: 'Assignment revoked', assignment_notified: 'Verifier notified',
  trello_card_created: 'Verification task created', revoked_card_archived: 'Verification task archived',
  banking_attached_to_matter_client: 'Instructions linked to another matter',
  banking_details_revealed: 'Bank details viewed', banking_edited: 'Bank details corrected',
  banking_superseded: 'Instructions replaced', replacement_request_linked: 'Replacement request linked',
  invitation_requested: 'Client request created', revocation_requested: 'Request rescinded',
  identity_images_viewed: 'Identity images opened', identity_photo_viewed: 'Identity image viewed',
  pin_send_reserved: 'Confirmation code requested', pin_sent: 'Confirmation code sent',
  pin_send_failed: 'Confirmation code could not be sent', pin_matched: 'Confirmation code matched',
  pin_mismatch: 'Confirmation code did not match', verification_completed: 'Verification completed',
  zoom_recording_attached: 'Zoom recording attached', zoom_recording_viewed: 'Zoom recording viewed',
};
const currency = cents => new Intl.NumberFormat('en-US', {style:'currency',currency:'USD'}).format(cents / 100);
const readable = key => key.replaceAll('_',' ').replace(/\bid\b/g,'ID').replace(/^./, first => first.toUpperCase());
const timestamp = value => { const date = new Date(value); return Number.isNaN(date.getTime()) ? null : date; };
const dayLabel = date => date ? date.toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'}) : 'Date unavailable';

function summary(event) {
  const details = event.details || {};
  if (event.action === 'assignment_pending' && typeof details.amount_cents === 'number') return `${currency(details.amount_cents)} to client`;
  if (event.action === 'banking_attached_to_matter_client' && details.target_matter) return `Linked to ${details.target_matter}`;
  if (event.action === 'verification_completed') return details.method === 'video' ? 'Verified by Zoom' : details.method === 'phone' ? 'Verified by phone' : '';
  return event.note || '';
}

function detailValue(key, value) {
  if (key === 'amount_cents' && typeof value === 'number') return currency(value);
  if (key === 'not_before' && timestamp(value)) return timestamp(value).toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value == null) return '—';
  return typeof value === 'object' ? JSON.stringify(value,null,2) : String(value);
}

export default function PaymentVerificationHistory({ decisions = [], events, onBack }) {
  const entries = [
    ...decisions.map((event,index)=>({...event,id:`decision-${index}`,at:event.recorded_at,title:event.label,actor:event.recorded_by})),
    ...(events || []).map(event=>({...event,id:`audit-${event.id}`,title:ACTIONS[event.action] || readable(event.action),actor:event.employee_name || 'System'})),
  ].sort((a,b)=>(timestamp(b.at)?.getTime() || 0)-(timestamp(a.at)?.getTime() || 0));
  return <section className="verification-history" aria-label="Full verification history">
    <header className="verification-history__heading">
      {onBack ? <AppButton variant="secondary" icon="pi pi-arrow-left" size="small" aria-label="Back to verification" tooltip="Back to verification" onClick={onBack}/> : null}
      <h3>Verification history</h3><span>{events === null ? 'Loading…' : `${entries.length} ${entries.length === 1 ? 'event' : 'events'}`}</span>
    </header>
    {events === null ? <p role="status">Loading activity…</p> : null}
    {events !== null && !entries.length ? <p className="verification-history__empty">No verification activity yet.</p> : null}
    <ol className="verification-history__rows">{entries.map(event=>{
      const date=timestamp(event.at);
      return <li key={event.id}>
        <article className="verification-history__event">
          <h5>{event.title}</h5>
          <p className="verification-history__metadata"><span>{event.actor || 'System'}</span><span aria-hidden="true">·</span><time dateTime={date ? event.at : undefined}>{dayLabel(date)}{date ? ` · ${date.toLocaleTimeString(undefined,{hour:'numeric',minute:'2-digit'})}` : ''}</time></p>
          {summary(event) ? <p className="verification-history__summary">{summary(event)}</p> : null}
          {event.details && Object.keys(event.details).length ? <details className="verification-history__audit"><summary>Audit details</summary>
            <dl>{Object.entries(event.details).map(([key,value])=><div key={key}><dt>{key === 'amount_cents' ? 'Client amount' : readable(key)}</dt><dd>{detailValue(key,value)}</dd></div>)}</dl>
          </details> : null}
        </article>
      </li>;
    })}</ol>
  </section>;
}
